import { getFullPath, splitPathIntoSegments } from '../../../../generic/FileTree/buildTreeUtils';
import type { SnippetTreeNodeData } from '../../types';

//  This is a sort of intermediate type while building the tree. The Map allows us to do a flat lookup for inserting new nodes.
type SnippetTreeBuilderNode = {
  path: string;
  children: Map<string, SnippetTreeBuilderNode>;
};

/**
 * Get a fresh node
 * @param {string} path - A given path segment
 */
const getNewNode = (path = ''): SnippetTreeBuilderNode => ({
  path,
  children: new Map(),
});

/**
 * Insert a path in the tree.
 * Doesn't return anything because it is setting the various children Maps
 * @param {SnippetTreeBuilderNode} root - The root node to insert children into.
 * @param {string} path - A full file path.
 */
function insertPath(root: SnippetTreeBuilderNode, path: string): void {
  let currentNode = root;

  //  Iterate each segment, injecting the children at the correct node
  splitPathIntoSegments(path).forEach(segment => {
    let currentSegment = currentNode.children.get(segment);
    //  If the current node doesn't have the segment, set it up
    if (!currentSegment) {
      currentSegment = getNewNode(segment);
      currentNode.children.set(segment, currentSegment);
    }
    //  "Step down" the tree now. We need to cast because Typescript thinks it's maybe undefined (despite the above code)
    currentNode = currentSegment;
  });
}

/**
 * Once we have built the tree, we want to collapse the children down to regular-degular SnippetTreeNodeData.
 * We need to know the full file path for the `path` on SnippetTreeNodeData (needs to be unique across the tree).
 * @param {SnippetTreeBuilderNode} node - A SnippetTreeBuilderNode element
 * @param {string} parentPath - The full path to the parent node.
 */
function collapse(node: SnippetTreeBuilderNode, parentPath = ''): SnippetTreeNodeData {
  //  Get the full file path so we can use it as a unique ID for nodes
  const fullPath = getFullPath(node.path, parentPath);
  const isRoot = fullPath === '/';

  //  Handle leaves first, since they don't have any children nodes anyway.
  const isLeaf = !node.children.size; // No children, you're a leaf.
  if (isLeaf && !isRoot) {
    return {
      type: 'file',
      name: node.path,
      path: fullPath,
    };
  }

  //  Recursively collapse all of the inner children.
  const children: SnippetTreeNodeData[] = Array.from(node.children.values())
    .map(child => collapse(child, fullPath));

  return {
    type: 'directory',
    name: node.path,
    path: fullPath,
    children,
  };
}

export { findFirstFilePath } from '../../../../generic/FileTree/buildTreeUtils';

/**
 * Takes a flat list of SnippetMatches and builds a trie-structure suitable
 * for passing to React Arborist.
 *
 * This is different from the main snippets tree view in that the entire
 * tree is known up-front (nothing needs to be fetched).
 *
 * @param {SnippetMatch[]} matches - List of snippet matches.
 */
export default function buildTreeFromMatches(matches: Array<{ path: string }>): SnippetTreeNodeData[] {
  //  Root starts at "/"
  const root = getNewNode('/');

  matches.forEach(match => {
    insertPath(root, match.path);
  });
  //  Recursively collapse all children nodes.
  //  We wrap this in an array because react-arborist expects a list of top-level nodes (in our case just one root).
  return [collapse(root)];
}
